import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { PageContainer } from "../../components/PageContainer";
import { Stepper } from "../../components/Stepper";
import { useFetchBillingData } from "../../hooks/useFetchBillingData";
import { useHandleLeavePrompt } from "../../hooks/useHandleLeavePrompt";
import { updateBillingCurrentStep } from "../../reducers/billingReducer";
import { getBillingAvailableSteps, getBillingCurrentStep, getBillingIsTouched } from "../../selectors/billing";
import { useAppDispatch } from "../../store";
import { BILLING_STEPS, BillingStep } from "../../types/billing";
import { MayorModulesType } from "../../types/mayorModules";
import { LeaveConfirmationModal } from "../WasteCollectionEditPage/LeaveConfirmationModal/LeaveConfirmationModal";
import { BillingHeader } from "./BillingHeader";
import { BillingPageBilling } from "./BillingPageBilling";
import { BillingPageCalculation } from "./BillingPageCalculation";
import { BillingPageCosts } from "./BillingPageCosts/BillingPageCosts";

import styles from "./BillingPage.module.scss";

export const BillingPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const currentStep = useSelector(getBillingCurrentStep);
  const isTouched = useSelector(getBillingIsTouched);
  const availableSteps = useSelector(getBillingAvailableSteps);

  useFetchBillingData();
  const { isModalVisible, toggleModalVisibility, handleContinue } = useHandleLeavePrompt(isTouched);

  const handleStepChange = (step: string) => {
    dispatch(updateBillingCurrentStep(step as BillingStep));
  };

  const handleModalContinue = () => {
    handleContinue();
  };

  const steps = useMemo(
    () =>
      BILLING_STEPS.map((step) => ({
        label: t(`billingPage.steps.${step}`),
        value: step,
        available: availableSteps.includes(step),
      })),
    [t, availableSteps]
  );

  return (
    <PageContainer
      module={MayorModulesType.BillingModule}
      HeaderComponent={BillingHeader}
      fullHeight={false}
      smallBottomPadding
    >
      <div className={styles.container}>
        <Stepper steps={steps} onStepChange={handleStepChange} currentStep={currentStep} />
        {currentStep === BillingStep.Costs && <BillingPageCosts />}
        {currentStep === BillingStep.Calculation && <BillingPageCalculation />}
        {currentStep === BillingStep.Billing && <BillingPageBilling />}
      </div>
      <LeaveConfirmationModal
        isModalVisible={isModalVisible}
        toggleModalVisibility={toggleModalVisibility}
        handleContinue={handleModalContinue}
      />
    </PageContainer>
  );
};
